import { request } from '@/lib/api'

/**
 * Рабочее место по обращениям клиентов (тикет #123): карточка и действия.
 *
 * Очередь — это список механизма списков (`support.ticket`), отдельного эндпоинта у неё нет.
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе.
 */

export type TicketStatus = 'open' | 'answered' | 'resolved' | 'closed'

export interface TicketEntry {
  id: string
  /** `message` — видит клиент; `note` — внутренняя заметка, клиенту не уходит никогда. */
  type: 'message' | 'note'
  side: 'client' | 'manager'
  authorUserId?: string | null
  authorName?: string | null
  body: string
  createdAt: string
}

export interface TicketCard {
  id: string
  number: string
  subject: string
  kind: string
  status: TicketStatus
  source: string
  counterpartyId: string
  counterpartyName?: string | null
  assignedUserId?: string | null
  assignedUserName?: string | null
  createdAt: string
  firstResponseDueAt: string
  firstAnswerAt?: string | null
  slaBreached: boolean
  /** Уложился ли первый ответ в срок; нет поля — ответа ещё не было. */
  slaMet?: boolean | null
  resolvedAt?: string | null
  rating?: number | null
  ratingComment?: string | null
  version: number
  /** Достаёт ли право `update` вызывающего до этого обращения. */
  mayUpdate: boolean
  /** Куда менеджер может перевести обращение сейчас. */
  transitions?: TicketStatus[]
  entries?: TicketEntry[]
  /** Пометка «срочно» в очереди: ставит правило маршрутизации или человек (#132). */
  urgent?: boolean
  /** route | rule | manual — как выбран исполнитель (#132). */
  assignedVia?: string | null
  /** Разбор ИИ (#132); нет `status` — ещё не разобрано. */
  triage?: TicketTriage | null
}

export type TriageStatus = 'analyzed' | 'unavailable' | 'failed' | 'rejected' | 'no_text'

/**
 * Разбор обращения ИИ (#132). Категория, тональность и срочность описывают последний успешный
 * разбор, `status` — последнюю попытку: если клиент дописал, а новый текст не разобран, на
 * карточке видно и прежний разбор, и то, что он прежний.
 */
export interface TicketTriage {
  status?: TriageStatus | null
  category?: string | null
  /** Вид обращения, к которому относится категория (`question` / `claim` / `request`). */
  categoryKind?: string | null
  tone?: string | null
  urgency?: string | null
  reason?: string | null
  message?: string | null
  triagedAt?: string | null
  routingRuleName?: string | null
}

export interface Assignee {
  id: string
  name: string
}

export function fetchTicket(id: string, signal?: AbortSignal): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}`, { signal })
}

/** `draftTaskId` — черновик ИИ, с которого начат ответ (#134); клиенту уходит `body`. */
export function answerTicket(id: string, body: string, draftTaskId: string | null = null): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}/messages`, { method: 'POST', body: { body, draftTaskId } })
}

export function noteTicket(id: string, body: string): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}/notes`, { method: 'POST', body: { body } })
}

export function assignTicket(id: string, userId: string | null): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}/assign`, { method: 'POST', body: { userId } })
}

export function changeTicketStatus(id: string, status: TicketStatus): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}/status`, { method: 'POST', body: { status } })
}

export function markTicketUrgent(id: string, urgent: boolean): Promise<TicketCard> {
  return request<TicketCard>(`/support/tickets/${id}/urgent`, { method: 'POST', body: { urgent } })
}

export function fetchAssignees(signal?: AbortSignal): Promise<Assignee[]> {
  return request<{ assignees?: Assignee[] }>('/support/assignees', { signal }).then((b) => b.assignees ?? [])
}
